import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { FeatureService } from '@maximizer/core/shared/data-access';
import { GlobalServicesLoginDetails } from '@maximizer/core/shared/domain';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import { AppDirectoryService } from '@maximizer/outlook-sync/data-access';
import {
  OutlookStore,
  releasePopupOpen,
  respondToHandshakeFromOpener,
  generateHandshakeNonce,
  SYNC_CONFIG_HANDSHAKE_NONCE_PARAM,
  SYNC_CONFIG_LANG_PARAM,
  tryClaimPopupOpen,
} from '@maximizer/outlook/shared/data-access';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { catchError, of } from 'rxjs';

@Component({
  selector: 'maximizer-outlook-sync-install',
  templateUrl: './install.component.html',
  imports: [KENDO_BUTTON, OUTLOOK_SHARED_UI, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SyncInstallComponent implements OnInit {
  private readonly oAuthService = inject(OAuth2Service);
  private readonly outlookStore = inject(OutlookStore);
  private readonly featureService = inject(FeatureService);
  private readonly appDirectoryService = inject(AppDirectoryService);

  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  closeTooltips = output<void>();
  openInstallSync = output<boolean>();
  componentType = input<'dialog' | 'tooltip'>('tooltip');
  loginDetails?: GlobalServicesLoginDetails;

  ngOnInit(): void {
    this.loginDetails = this.oAuthService.getStorageLoginDetails();
  }

  async installOutlookSync(): Promise<void> {
    if (!this.loginDetails) {
      return;
    }

    if (this.componentType() === 'dialog') {
      this.openInstallSync.emit(false);
    } else {
      this.closeTooltips.emit();
    }

    const useNewFlow = this.featureService.isFeatureOn(
      'integration-o365-sync-configuration-shown',
      false,
    );

    if (!useNewFlow) {
      this.appDirectoryService
        .buildAppStoreFormRequest(this.loginDetails, true)
        .pipe(
          catchError(() => {
            this.notification().showMessage(
              'error',
              'outlook.errors.appStore',
              true,
            );
            return of(null);
          }),
        )
        .subscribe();
      return;
    }

    if (!tryClaimPopupOpen()) {
      return;
    }

    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      releasePopupOpen();
      this.notification().showMessage(
        'error',
        'outlook.errors.appStore',
        true,
      );
      return;
    }
    const handshakeNonce = generateHandshakeNonce();
    respondToHandshakeFromOpener(popup, {
      oAuthService: this.oAuthService,
      isCloud: this.outlookStore.isCloud(),
      nonce: handshakeNonce,
    });

    const installUrl = new URL(
      `/sync-configuration?install=true&${SYNC_CONFIG_HANDSHAKE_NONCE_PARAM}=${encodeURIComponent(handshakeNonce)}&${SYNC_CONFIG_LANG_PARAM}=${encodeURIComponent(this.outlookStore.language())}`,
      window.location.origin,
    ).toString();
    popup.location.href = installUrl;
  }
}
